import type { DefaultTheme } from 'styled-components';
import styled, { css } from 'styled-components';
import type { ReactNode, JSX } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isNotNil } from '~/utilities/type-guards';
import { v4 as uuidv4 } from 'uuid';
import { matchString } from '~/utilities/matchers';
import { Button } from '@wistia/vhs';
import type { Position } from '~/onboarding/types';
import { TooltipContent } from '../Tooltip/shared/TooltipContent';
import { TargetPortal } from '../TargetPortal';

const tooltipTopStyle = css`
  left: 50%;
  padding: 8px 0;
  top: 0;
  transform: translate(-50%, -100%);
`;

const tooltipBottomStyle = css`
  bottom: 0;
  left: 50%;
  padding: 8px 0;
  transform: translate(-50%, 100%);
`;

const tooltipLeftStyle = css<CommonTooltipProps>`
  left: 0;
  padding: 0 8px;
  top: 0;
  transform: ${({ variant }) =>
    variant === 'compact' ? 'translate(-100%, -25%)' : 'translateX(-100%)'};
`;

const tooltipRightStyle = css<CommonTooltipProps>`
  padding: 0 8px;
  right: 0;
  top: 0;
  transform: ${({ variant }) =>
    variant === 'compact' ? 'translate(100%, -25%)' : 'translateX(100%)'};
`;

const tooltipBottomRightStyle = css<CommonTooltipProps>`
  bottom: 0;
  padding: 0 8px;
  right: 0;
  transform: ${({ variant }) =>
    variant === 'compact' ? 'translate(100%, -25%)' : 'translateX(100%)'};
`;

export const tooltipStyle = css`
  background: white;
  border-radius: 16px;
  box-shadow: ${({ theme }: { theme: DefaultTheme }) => theme.shadow.elevation2};
  max-width: 270px;
  padding: 16px;
`;

const compactTooltipStyle = css`
  border-radius: 4px;
  box-shadow: ${({ theme }) => theme.shadow.elevation1};
  padding: ${({ theme }) => theme.spacing.space03};
`;

export const Container = styled.div`
  display: inline-block;
  position: relative;
`;

export const TooltipContainer = styled.div<
  CommonTooltipProps & {
    active: boolean;
    direction: Directions;
  }
>`
  display: ${({ active }) => (active ? 'block' : 'none')};
  position: absolute;
  z-index: ${({ theme }: { theme: DefaultTheme }) => theme.zIndex.Tooltip};
  ${({ direction }) =>
    matchString(direction)({
      top: () => tooltipTopStyle,
      bottom: () => tooltipBottomStyle,
      'bottom-right': () => tooltipBottomRightStyle,
      left: () => tooltipLeftStyle,
      right: () => tooltipRightStyle,
    })}
`;

const TootipButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const StyledTooltip = styled.div<CommonTooltipProps & { fixedWidth: boolean; width: string }>`
  ${tooltipStyle}
  width: ${({ fixedWidth, width }) => (fixedWidth ? '270px' : width)};

  ${({ variant }) => variant === 'compact' && compactTooltipStyle}
`;

type Directions = Extract<Position, 'bottom-right' | 'bottom' | 'left' | 'right' | 'top'>;

type TooltipVariant = 'compact';

type CommonTooltipProps = { variant?: TooltipVariant };

type FancyTooltipProps = {
  delay?: number;
  direction: Directions;
  fixedWidth?: boolean;
  image?: JSX.Element;
  content?: ReactNode;
  children: ReactNode;
  upgradeText?: string;
  title?: string;
  id?: string;
  startActive?: boolean;
  width?: string;
  disableHover?: boolean;
  variant?: TooltipVariant;
  onDismiss?: () => void;
  dismissible?: boolean;
  disableFocus?: boolean;
  portalElementId?: string;
};

export const FancyTooltip = ({
  delay,
  direction,
  fixedWidth,
  image,
  content,
  children,
  upgradeText,
  title,
  id,
  startActive = false,
  width,
  disableHover = false,
  variant = undefined,
  onDismiss,
  dismissible = false,
  disableFocus = false,
  portalElementId = undefined,
}: FancyTooltipProps): JSX.Element => {
  const [active, setActive] = useState<boolean>(startActive);
  const tooltipEl = useRef(null);
  const tooltipId = useMemo(() => id ?? uuidv4(), [id]);
  const tooltipWidth = isNotNil(width) ? width : 'auto';
  let timer: NodeJS.Timeout | null = null;

  const getTarget = useCallback(
    () => (isNotNil(portalElementId) ? document.getElementById(portalElementId) : null),
    [portalElementId],
  );

  const startTimer = () => {
    if (isNotNil(timer)) {
      clearTimeout(timer);
    }
    timer = setTimeout(() => {
      setActive(true);
    }, delay);
  };

  useEffect(() => {
    const keyDownHandler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setActive(false);
      }
    };

    document.addEventListener('keydown', keyDownHandler);

    return () => {
      document.removeEventListener('keydown', keyDownHandler);
    };
  }, []);

  const handleMouseOver = () => {
    if (disableHover) {
      return;
    }

    if (isNotNil(delay)) {
      startTimer();
    } else {
      setActive(true);
    }
  };

  const handleMouseLeave = () => {
    if (disableHover) {
      return;
    }

    if (isNotNil(timer)) {
      clearTimeout(timer);
    }
    setActive(false);
  };

  const handleDismiss = () => {
    setActive(false);
    onDismiss?.();
  };

  const handleFocus = () => {
    if (disableFocus) {
      return;
    }
    setActive(true);
  };

  const tooltipComponent = (
    <TooltipContainer
      active={active}
      aria-hidden={!active}
      data-testid="tooltip-content"
      direction={direction}
      id={tooltipId}
      role="tooltip"
      variant={variant}
    >
      <StyledTooltip
        fixedWidth={fixedWidth ?? false}
        width={tooltipWidth}
        variant={variant}
      >
        <TooltipContent
          image={image}
          content={content}
          upgradeText={upgradeText}
          title={title}
        />
        {isNotNil(onDismiss) || dismissible ? (
          <TootipButtonContainer>
            <Button
              variant="secondary"
              onClick={handleDismiss}
              label="Dismiss"
              size="md"
            />
          </TootipButtonContainer>
        ) : null}
      </StyledTooltip>
    </TooltipContainer>
  );
  return (
    <Container
      ref={tooltipEl}
      aria-labelledby={tooltipId}
      data-testid="tooltip-button"
      onBlur={() => setActive(false)}
      onFocus={handleFocus}
      onMouseLeave={handleMouseLeave}
      onMouseOver={handleMouseOver}
      role="button"
      tabIndex={0}
    >
      {portalElementId != null ? (
        <TargetPortal getTarget={getTarget}>{tooltipComponent}</TargetPortal>
      ) : (
        tooltipComponent
      )}
      {children}
    </Container>
  );
};
